import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { paths } from '@/app/router/paths';
import { BillingPeriodToggle } from '@/features/subscriptions/components/billing-period-toggle';
import { PricingCard } from '@/features/subscriptions/components/pricing-card';
import { addonsPlans } from '@/features/subscriptions/data/addons-plans';
import { organizationPlans } from '@/features/subscriptions/data/organization-plans';
import { projectPlans } from '@/features/subscriptions/data/project-plans';
import { useCanManageSubscriptions } from '@/features/subscriptions/hooks/use-can-manage-subscriptions';
import { BillingPeriod } from '@/features/subscriptions/types/subscription-types';
import WarningTriangleIcon from '@/shared/assets/warning-triangle.png';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog';

interface PaywallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: 'project' | 'organization' | 'addon';
  projectId?: number | null;
}

export const PaywallDialog = ({
  open,
  onOpenChange,
  context,
  projectId,
}: PaywallDialogProps) => {
  const { t } = useTranslation('subscriptions');
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('yearly');
  const canManageSubscriptions = useCanManageSubscriptions();

  function handleCancelAndCompare() {
    onOpenChange(false);
    window.location.href = paths.plansManagement.path;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1200px] border border-white/10 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f1729] p-0">
        <div className="flex flex-col gap-8 p-8">
          <div className="flex flex-col gap-5">
            <div className="flex items-start gap-3">
              <img
                src={WarningTriangleIcon}
                alt="warning"
                className="size-6 object-contain"
              />
              <div className="flex flex-1 flex-col gap-2">
                <DialogTitle className="text-2xl font-semibold text-white">
                  {canManageSubscriptions
                    ? t('paywall.title')
                    : t('paywall.memberRestriction.title')}
                </DialogTitle>
                <DialogDescription className="text-sm text-white/60">
                  {canManageSubscriptions
                    ? t('paywall.description')
                    : t('paywall.memberRestriction.description')}
                </DialogDescription>
              </div>
            </div>

            {canManageSubscriptions && (
              <div className="flex w-full justify-end">
                <BillingPeriodToggle
                  billingPeriod={billingPeriod}
                  onBillingPeriodChange={setBillingPeriod}
                />
              </div>
            )}
          </div>

          {canManageSubscriptions && (
            <>
              {context === 'project' ? (
                <div className="flex flex-col gap-16">
                  <PlanSection
                    title={t('paywall.projectPlans.title')}
                    description={t('paywall.projectPlans.description')}
                    plans={projectPlans}
                    billingPeriod={billingPeriod}
                    projectId={projectId}
                    onAssignSuccess={() => onOpenChange(false)}
                  />
                  <PlanSection
                    title={t('paywall.addons.title')}
                    description={t('paywall.addons.description')}
                    plans={addonsPlans}
                    billingPeriod={billingPeriod}
                    projectId={projectId}
                    onAssignSuccess={() => onOpenChange(false)}
                  />
                </div>
              ) : context === 'organization' ? (
                <PlanSection
                  title={t('paywall.organizationPlans.title')}
                  description={t('paywall.organizationPlans.description')}
                  plans={organizationPlans}
                  billingPeriod={billingPeriod}
                />
              ) : (
                <PlanSection
                  title={t('paywall.addons.title')}
                  description={t('paywall.addons.description')}
                  plans={addonsPlans}
                  billingPeriod={billingPeriod}
                  projectId={projectId}
                  onAssignSuccess={() => onOpenChange(false)}
                />
              )}

              <div className="flex flex-col gap-4 border-t border-white/10 pt-6">
                <Button
                  variant="ghost"
                  className="w-full text-center text-sm text-white/60 hover:text-white"
                  onClick={handleCancelAndCompare}
                >
                  {t('paywall.cancelAndCompare')}
                </Button>
                <p className="text-center text-xs text-white/60">
                  {t('paywall.supportText')}
                </p>
              </div>
            </>
          )}

          {!canManageSubscriptions && (
            <div className="flex justify-center border-t border-white/10 pt-6">
              <Button
                variant="outline"
                className="min-w-40 border-white/20 bg-white/5 text-white hover:bg-white/10"
                onClick={() => onOpenChange(false)}
              >
                OK
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface PlanSectionProps {
  title: string;
  description?: string;
  plans: typeof projectPlans | typeof organizationPlans | typeof addonsPlans;
  billingPeriod: BillingPeriod;
  projectId?: number | null;
  onAssignSuccess?: () => void;
}

const PlanSection = ({
  title,
  description,
  plans,
  billingPeriod,
  projectId,
  onAssignSuccess,
}: PlanSectionProps) => {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <span className="text-2xl font-semibold text-white">{title}</span>
        {description && (
          <span className="text-sm text-white/60">{description}</span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {plans.map((plan) => (
          <PricingCard
            key={plan.id}
            plan={plan}
            billingPeriod={billingPeriod}
            projectId={projectId}
            onAssignSuccess={onAssignSuccess}
          />
        ))}
      </div>
    </div>
  );
};
